import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast, SpinLoading } from 'antd-mobile';
import {
  RightOutline,
  SearchOutline,
  EditSOutline,
  EnvironmentOutline,
  EyeInvisibleOutline,
  EyeOutline
} from 'antd-mobile-icons';
import { motion } from 'framer-motion';
import SafeArea from '@/components/SafeArea';
import EmptyState from '@/components/EmptyState';
import Skeleton from '@/components/Skeleton';
import Avatar from '@/components/Avatar';
import { getSalesOverview } from '@/api/statistics';
import { getSales } from '@/api/sales';
import dayjs from 'dayjs';

interface OverviewData {
  today: {
    total_weight: number;
    total_amount: number;
    sale_count: number;
  };
  thisMonth: {
    total_weight: number;
    total_amount: number;
    sale_count: number;
  };
  totalCustomers: number;
}

const WEEKDAYS = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
const HOME_SENSITIVE_DATA_KEY = 'wine_home_sensitive_data_v1';

export default function Home() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showSensitiveData, setShowSensitiveData] = useState(
    () => localStorage.getItem(HOME_SENSITIVE_DATA_KEY) !== 'hidden'
  );

  const toggleSensitiveData = () => {
    setShowSensitiveData((current) => {
      const next = !current;
      localStorage.setItem(HOME_SENSITIVE_DATA_KEY, next ? 'visible' : 'hidden');
      return next;
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [overviewRes, salesRes] = await Promise.all([
        getSalesOverview(),
        getSales({ limit: 5 })
      ]);

      if (overviewRes.code === 0) {
        setOverview(overviewRes.data);
      }
      if (salesRes.code === 0) {
        setRecentSales(salesRes.data || []);
      }
    } catch (error) {
      console.error(error);
      setLoadError(true);
      Toast.show({ content: '加载首页数据失败', icon: 'fail' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const recentRecords = useMemo(() => recentSales.slice(0, 5), [recentSales]);

  const now = dayjs();
  const dateLabel = `${now.format('M月D日')} · ${WEEKDAYS[now.day()]}`;

  return (
    <div className="ios-page han-zhou-home">
      <section className="home-brand-plane">
        <SafeArea>
          <header className="home-brand-header">
            <div>
              <div className="home-brand-name">汉州醉</div>
              <div className="home-brand-subtitle">{dateLabel}</div>
            </div>
            <button
              className="home-search-button"
              onClick={() => navigate('/search', { replace: true })}
              aria-label="搜索"
              data-haptic="selection"
            >
              <SearchOutline />
            </button>
          </header>
        </SafeArea>

        <motion.div
          className="home-hero-content"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24 }}
        >
          <div className="home-hero-label-row">
            <div className="home-hero-label">今日销售</div>
            <button
              type="button"
              className="home-privacy-toggle"
              onClick={toggleSensitiveData}
              aria-label={showSensitiveData ? '隐藏销售数据' : '显示销售数据'}
              aria-pressed={!showSensitiveData}
              data-haptic="selection"
            >
              {showSensitiveData ? <EyeOutline /> : <EyeInvisibleOutline />}
            </button>
          </div>
          {loading ? (
            <div className="ios-skeleton home-hero-skeleton" />
          ) : (
            <div className="home-hero-amount"><span>¥</span>{showSensitiveData ? (overview ? Number(overview.today.total_amount).toFixed(0) : '0') : '***'}</div>
          )}
          <div className="home-hero-meta">
            {showSensitiveData
              ? `${overview ? Number(overview.today.total_weight).toFixed(1) : '0'} 斤 · ${overview?.today.sale_count || 0} 笔`
              : '*** 斤 · *** 笔'}
          </div>
          <motion.button
            className="home-hero-action"
            onClick={() => navigate('/sale')}
            whileTap={{ scale: 0.97 }}
            data-haptic="medium"
          >
            <EditSOutline fontSize={25} /> 记一笔
          </motion.button>
        </motion.div>
      </section>

      {loading ? (
        <div className="home-summary-loading"><SpinLoading color="primary" /></div>
      ) : (
        <div className="home-summary-card">
          <div><span>销售斤数</span><strong>{showSensitiveData ? Number(overview?.today.total_weight || 0).toFixed(1) : '***'}{showSensitiveData ? <em>斤</em> : null}</strong></div>
          <div><span>销售笔数</span><strong>{showSensitiveData ? overview?.today.sale_count || 0 : '***'}{showSensitiveData ? <em>笔</em> : null}</strong></div>
        </div>
      )}

      {loading ? (
        <div className="home-recent-card">
          <Skeleton count={3} />
        </div>
      ) : loadError ? (
        <div className="home-empty-state">
          <EmptyState
            title="首页数据加载失败"
            description="请检查网络后重试"
            action={(
              <button type="button" className="ios-button ios-button-primary" onClick={() => void fetchData()}>
                重新加载
              </button>
            )}
          />
        </div>
      ) : recentRecords.length > 0 ? (
        <section className="home-recent-card">
          <div className="home-recent-heading"><h2>最近销售</h2><button onClick={() => navigate('/statistics?from=home')}>查看统计 <RightOutline fontSize={16} /></button></div>
          {recentRecords.map((record) => (
            <button
              key={`sale-${record.id}`}
              className="home-record-row"
              onClick={() => navigate(`/sale/${record.id}/edit?from=home`)}
              data-haptic="light"
            >
              <div className="home-record-time">
                {dayjs(record.sale_time).isSame(now, 'day')
                  ? dayjs(record.sale_time).format('HH:mm')
                  : dayjs(record.sale_time).format('M/D')}
              </div>
              <Avatar className="home-record-avatar" src={record.customer_avatar} name={record.customer_name} size={42} />
              <div className="home-record-main"><strong>{record.customer_name}</strong><span><EnvironmentOutline /> {record.customer_village || '未填写村'}</span><p>{showSensitiveData ? `${record.category_name} · ${record.price_level_name} × ${record.weight}${record.unit || '斤'}` : `${record.category_name} · ***`}</p></div>
              <div className="home-record-money">{showSensitiveData ? `¥${record.total_amount}` : '***'}<RightOutline /></div>
            </button>
          ))}
        </section>
      ) : (
        <div className="home-empty-state">
          <EmptyState title="暂无动态" description="去记一笔销售吧" />
        </div>
      )}
    </div>
  );
}
